import React from 'react'
import { useTranslation } from 'react-i18next'
import { Gem, Swords, Trophy } from 'lucide-react'
import AnimatedNumber from '../../../components/AnimatedNumber'
import { CARD_CLASS, TILE_CLASS } from '../utils/teamUi'
import { formatSigned, profitClass } from '../utils/teamStats'
import { formatPrice } from '../../../utils/formatters'

/**
 * Итоги банды за период (docs/team-stats-api.md, §3, блок `totals`).
 *
 * Победа банды — раунд или сессия, где состав в сумме получил больше, чем
 * поставил. `profit` бывает отрицательным, `given` — кристаллы, которые
 * победы раздали тиммейтам (ненулевой только в классике и кейсбатле).
 */
const TeamStatsTotals = ({ totals, accent }) => {
  const { t } = useTranslation()
  const data = totals || {}
  const profitPercent = Number(data.bet) > 0 ? Math.round(Number(data.profit ?? 0) / Number(data.bet) * 10000) / 100 : null

  const tiles = [
    { key: 'bet', label: t('team.stats.bet', 'Поставлено'), value: formatPrice(data.bet ?? 0) },
    { key: 'win', label: t('team.stats.win', 'Получено'), value: formatPrice(data.win ?? 0) },
    {
      key: 'profit',
      label: t('team.stats.profit', 'Итог'),
      value: profitPercent == null ? '—' : `${formatSigned(profitPercent)}%`,
      className: profitClass(profitPercent)
    },
    { key: 'xp', label: t('team.stats.xp', 'XP банде'), value: formatPrice(data.xp ?? 0) },
    // Возвраты классика и доли кейсбатла: в остальных режимах всегда 0
    { key: 'given', label: t('team.stats.given', 'Передано своим'), value: formatPrice(data.given ?? 0) }
  ]

  return (
    <div className={`${CARD_CLASS} relative overflow-hidden p-4`}>
      <div
        className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full blur-2xl"
        style={{ backgroundColor: `${accent}1A` }}
      />

      <div className="relative grid grid-cols-2 gap-2">
        <div className={`${TILE_CLASS} bg-dark-primary`}>
          <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-text-secondary">
            <Swords size={12} style={{ color: accent }} />
            {t('team.stats.games', 'Командных игр')}
          </div>
          <div className="text-2xl font-bold tabular-nums text-white">
            <AnimatedNumber value={data.games ?? 0} />
          </div>
        </div>

        <div className={`${TILE_CLASS} bg-dark-primary`}>
          <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-text-secondary">
            <Trophy size={12} style={{ color: accent }} />
            {t('team.stats.wins', 'Побед')}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold tabular-nums text-white">
              <AnimatedNumber value={data.wins ?? 0} />
            </span>
            <span className="text-xs font-semibold tabular-nums" style={{ color: accent }}>
              {data.win_rate ?? 0}%
            </span>
          </div>
        </div>
      </div>

      <div className="relative mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {tiles.map(({ key, label, value, className }) => (
          <div key={key} className={`${TILE_CLASS} bg-dark-primary`}>
            <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-text-secondary">
              <Gem size={11} style={{ color: accent }} />
              <span className="truncate">{label}</span>
            </div>
            <p className={`truncate text-sm font-bold tabular-nums ${className || 'text-white'}`}>{value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TeamStatsTotals
